import React, { useEffect, useState } from "react";
import getTimeZones from "../../services/timeZoneService";
import "./CurrentTimeClock.scss";

const HMS = {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

const DAY_SECONDS = 24 * 3600;

// Store opening_hours.day uses lowercase English weekday names ("monday" … "sunday").
function weekdayKey(date, timeZone) {
  return new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long" }).format(date).toLowerCase();
}

function makeFormatters(timeZone) {
  return {
    time: new Intl.DateTimeFormat("en-GB", { timeZone, ...HMS }),
    day: new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "long" }),
  };
}

const staticFormatters = {
  UTC: makeFormatters("UTC"),
  Paris: makeFormatters("Europe/Paris"),
};

function buildStoreFormatters(code) {
  if (!code) return null;
  try {
    return makeFormatters(code);
  } catch (err) {
    // Unknown / non-IANA timezone code — fall back to no store clock.
    return null;
  }
}

function parseTimeToSeconds(value) {
  if (!value) return null;
  const [h, m, s] = value.split(":").map(Number);
  return h * 3600 + m * 60 + (s || 0);
}

function humanizeDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}min`;
  return "<1min";
}

// Countdown when the transition is within 24h, otherwise the absolute day + time (in the store
// timezone) of the instant `deltaSeconds` from `now`.
function describeTransition(deltaSeconds, now, timeZone) {
  if (deltaSeconds < DAY_SECONDS) return `in ${humanizeDuration(deltaSeconds)}`;
  const target = new Date(now.getTime() + deltaSeconds * 1000);
  const day = new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short" }).format(target);
  const time = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hour12: false }).format(
    target
  );
  return `${day} ${time}`;
}

function ruleForDay(openingHours, dayKey) {
  return openingHours.find((o) => o.day === dayKey) || openingHours.find((o) => o.day === "default") || null;
}

function isOpenRule(rule) {
  return !!(rule && rule.is_open && rule.time_open && rule.time_close);
}

/**
 * Build the store's open intervals in seconds relative to today 00:00, from yesterday through the
 * next week. Overnight intervals (close <= open) simply extend past midnight into the following
 * day, so an early-morning "still open from yesterday" window needs no special handling. Opening
 * hours use one interval per day plus an is_open flag, with a "default" row as fallback.
 */
function buildOpenIntervals(openingHours, now, timeZone) {
  const intervals = [];
  for (let offset = -1; offset <= 7; offset += 1) {
    const dayKey = weekdayKey(new Date(now.getTime() + offset * DAY_SECONDS * 1000), timeZone);
    const rule = ruleForDay(openingHours, dayKey);
    if (!isOpenRule(rule)) continue;
    const open = parseTimeToSeconds(rule.time_open);
    const close = parseTimeToSeconds(rule.time_close);
    const start = offset * DAY_SECONDS + open;
    const end = start + ((close - open + DAY_SECONDS) % DAY_SECONDS || DAY_SECONDS);
    intervals.push({ start, end });
  }
  return intervals.sort((a, b) => a.start - b.start);
}

/**
 * Determine whether the store is currently open (in its own timezone) and when the next
 * open/close transition happens. `nowSeconds` is the current time-of-day in the store timezone.
 */
function computeStoreStatus(openingHours, now, timeZone, nowSeconds) {
  if (!openingHours || openingHours.length === 0) return null;

  const intervals = buildOpenIntervals(openingHours, now, timeZone);

  const current = intervals.find((i) => nowSeconds >= i.start && nowSeconds < i.end);
  if (current) {
    if (current.end - current.start >= DAY_SECONDS) return { open: true, detail: "24/7" };
    return { open: true, detail: `closes ${describeTransition(current.end - nowSeconds, now, timeZone)}` };
  }

  const next = intervals.find((i) => i.start > nowSeconds);
  if (next) {
    return { open: false, detail: `opens ${describeTransition(next.start - nowSeconds, now, timeZone)}` };
  }

  return { open: false, detail: "" };
}

function TimeTile({ label, zone, time, day, primary, status }) {
  return (
    <div className={`time-tile${primary ? " primary" : ""}`}>
      <div className="tile-head">
        <span className="tile-label">{label}</span> · {zone}
      </div>
      <div className="tile-body">
        <span className="tile-value">{time}</span>
        <span className="tile-day">{day}</span>
      </div>
      {status && (
        <div className={`tile-status${status.open ? " open" : " closed"}`}>
          <span className="dot" />
          <span>
            {status.open ? "Open" : "Closed"}
            {status.detail ? ` · ${status.detail}` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

function CurrentTimeClock({ storeTimezoneId, openingHours }) {
  const [now, setNow] = useState(new Date());
  const [storeTimezoneCode, setStoreTimezoneCode] = useState(null);

  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (storeTimezoneId == null) {
      setStoreTimezoneCode(null);
      return undefined;
    }
    getTimeZones()
      .then(({ data: timezones }) => {
        if (cancelled) return;
        const match = timezones.find((timezone) => timezone.id === storeTimezoneId);
        setStoreTimezoneCode(match ? match.code : null);
      })
      .catch(() => {
        if (!cancelled) setStoreTimezoneCode(null);
      });
    return () => {
      cancelled = true;
    };
  }, [storeTimezoneId]);

  const storeFormatters = buildStoreFormatters(storeTimezoneCode);

  let storeStatus = null;
  if (storeFormatters) {
    const nowSeconds = parseTimeToSeconds(storeFormatters.time.format(now));
    if (nowSeconds != null) {
      storeStatus = computeStoreStatus(openingHours, now, storeTimezoneCode, nowSeconds);
    }
  }

  return (
    <div className="current-time-clock">
      {storeFormatters && (
        <TimeTile
          label="Store local"
          zone={storeTimezoneCode}
          time={storeFormatters.time.format(now)}
          day={storeFormatters.day.format(now)}
          status={storeStatus}
          primary
        />
      )}
      <TimeTile
        label="UTC"
        zone="Coordinated"
        time={staticFormatters.UTC.time.format(now)}
        day={staticFormatters.UTC.day.format(now)}
      />
      <TimeTile
        label="Paris"
        zone="Europe/Paris"
        time={staticFormatters.Paris.time.format(now)}
        day={staticFormatters.Paris.day.format(now)}
      />
    </div>
  );
}

export default CurrentTimeClock;
